import React, { useState, useEffect } from "react";
import { X, Check } from "lucide-react";

const ViewAnalysisModal = ({ isOpen, onClose, analysisData }) => {
  const [state, setState] = useState({
    targetOccupation: "",
    skillsData: null,
    activeTab: "matching",
    skillsError: null
  });

  useEffect(() => {
    if (analysisData && isOpen) {
      const { targetOccupation, matchingSkills, skillsGap } = analysisData;
      
      const skillsData = {
        "Matching Skills": matchingSkills || [],
        "Skills Gap": skillsGap || []
      };

      setState({
        targetOccupation: targetOccupation || "Unknown Role",
        skillsData: skillsData,
        activeTab: "matching",
        skillsError: null
      });
    }
  }, [analysisData, isOpen]);

  const setPartial = (obj) => setState((prevState) => ({ ...prevState, ...obj }));

  const isSkillMatching = (skill) => {
    return (state.skillsData?.["Matching Skills"] || []).includes(skill);
  };

  const getSkillsCount = (isMatching, skillsData) => {
    if (!skillsData) return 0;
    return isMatching 
      ? (skillsData["Matching Skills"] || []).length
      : (skillsData["Skills Gap"] || []).length;
  };

  const renderStats = () => (
    <div className="bg-gray-50 border rounded-lg p-4 mb-4 grid grid-cols-2 text-center">
      <div>
        <div className="text-2xl font-bold text-green-600">{getSkillsCount(true, state.skillsData)}</div>
        <p className="text-xs font-bold text-gray-600">Matching Skills</p>
      </div>
      <div>
        <div className="text-2xl font-bold text-red-600">{getSkillsCount(false, state.skillsData)}</div>
        <p className="text-xs font-bold text-gray-600">Skills Gap</p>
      </div>
    </div>
  );

  const renderSkillsList = () => {
    const skillsToShow = state.activeTab === "matching" 
      ? (state.skillsData?.["Matching Skills"] || [])
      : (state.skillsData?.["Skills Gap"] || []);

    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-3">
          <h4 className="font-semibold text-[#6e28cc] mb-2 text-sm">
            {state.activeTab === "matching" ? "Matching Skills" : "Skills Gap"}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {skillsToShow.map((skill, i) => {
              const match = isSkillMatching(skill);
              return (
                <div 
                  key={i} 
                  className={`flex items-center gap-2 p-2 border rounded-lg text-sm ${
                    match 
                      ? "border-gray-300 text-gray-700" 
                      : "border-red-400 text-red-600"
                  }`}
                >
                  {match ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  {skill}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (state.skillsError) {
      return (
        <div className="text-center py-8">
          <p className="text-yellow-800 mb-2">{state.skillsError}</p>
        </div>
      );
    }
    
    if (!state.skillsData) {
      return <p className="text-center py-8 text-gray-500">No skills data available.</p>;
    }

    return (
      <>
        {renderStats()}
        <div className="flex gap-2 mb-4">
          {["matching", "gap"].map(tab => (
            <button 
              key={tab} 
              onClick={() => setPartial({ activeTab: tab })}
              className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                state.activeTab === tab 
                  ? (tab === "matching" ? "bg-green-600 text-white" : "bg-red-600 text-white") 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab === "matching" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {tab[0].toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        {renderSkillsList()}
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1400] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-center relative">
          <div className="bg-purple-100 rounded-xl p-3 text-center">
            <div className="text-xs text-purple-700 mb-1">Target Occupation</div>
            <div className="font-semibold text-sm text-purple-800">{state.targetOccupation}</div>
          </div>
          <button
            onClick={onClose}
            className="absolute right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            {renderContent()}
          </div>
        </div>


      </div>
    </div>
  );
};

export default ViewAnalysisModal;
